import { RefreshCw, ServerCog } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  fetchScraperForumStatus,
  type ScraperForumStatus as ForumStatus,
  type ScraperStatusResponse,
} from "../api/status";

const STATUS_META: Record<string, { label: string; className: string }> = {
  SUCCESS: { label: "Success", className: "sfs-status--success" },
  FAILED: { label: "Failed", className: "sfs-status--failed" },
  WAITING: { label: "Waiting", className: "sfs-status--waiting" },
  PARTIAL: { label: "Partial", className: "sfs-status--partial" },
  COOLDOWN: { label: "Cooldown", className: "sfs-status--cooldown" },
  OVERDUE: { label: "Overdue", className: "sfs-status--overdue" },
  RUNNING: { label: "Running", className: "sfs-status--running" },
  DISABLED: { label: "Disabled", className: "sfs-status--disabled" },
  "NEVER RUN": { label: "Never run", className: "sfs-status--disabled" },
};

const STATUS_ORDER: Record<string, number> = {
  FAILED: 0,
  COOLDOWN: 1,
  OVERDUE: 2,
  "NEVER RUN": 3,
  WAITING: 4,
  PARTIAL: 5,
  RUNNING: 6,
  SUCCESS: 7,
  DISABLED: 8,
};

function ago(value: string | null) {
  if (!value) return "Never";
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function valueOrDash(value: number | null) {
  return value == null ? "—" : value.toLocaleString();
}

function StatusPill({ status }: { status: string }) {
  const meta = STATUS_META[status] ?? { label: status, className: "sfs-status--disabled" };
  return <span className={`sfs-status ${meta.className}`}><span aria-hidden="true" />{meta.label}</span>;
}

function ForumStatusTable({ forums }: { forums: ForumStatus[] }) {
  const ordered = [...forums].sort((a, b) =>
    (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99) || a.forum.localeCompare(b.forum)
  );
  return (
    <div className="sfs-scroll">
      <table className="sfs-table">
        <thead><tr><th>Forum</th><th>Last check</th><th>Status</th><th>URLs</th><th>New</th><th>Errors</th><th>24h leaks</th></tr></thead>
        <tbody>{ordered.map((forum) => (
          <tr key={forum.forum}>
            <td className="sfs-forum">{forum.forum}</td>
            <td>{ago(forum.last_check)}</td>
            <td><StatusPill status={forum.status} /></td>
            <td>{valueOrDash(forum.urls)}</td>
            <td>{valueOrDash(forum.new)}</td>
            <td>{valueOrDash(forum.errors)}</td>
            <td>{valueOrDash(forum.leaks_24h)}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

export default function ScraperForumStatus() {
  const [data, setData] = useState<ScraperStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      setData(await fetchScraperForumStatus());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const interval = window.setInterval(() => void load(), 60_000);
    return () => window.clearInterval(interval);
  }, [load]);

  const statusCounts = data?.summary.status_counts ?? {};
  return (
    <section className="adm-section">
      <div className="adm-card sfs-card">
        <div className="adm-section-header">
          <div>
            <div className="adm-section-title-row">
              <div className="adm-section-icon sfs-icon"><ServerCog size={18} /></div>
              <span className="adm-section-label">Scraper Forum Status</span>
            </div>
            <p className="sfs-subtitle">Live operational status of configured scraper forums</p>
          </div>
          {data && <div className="sfs-header-meta"><span className="adm-count-chip">{data.summary.total} forums</span><span>Last updated: {ago(data.updated_at)}</span></div>}
        </div>

        {loading && !data ? <div className="sfs-state">Loading scraper status...</div> : error && !data ? (
          <div className="sfs-state">Unable to load scraper status.<button onClick={() => { setLoading(true); void load(); }}>Retry</button></div>
        ) : !data || data.forums.length === 0 ? <div className="sfs-state">No scraper status available.</div> : <>
          {error && <div className="sfs-inline-error">Unable to refresh scraper status.<button onClick={() => void load()}>Retry</button></div>}
          <div className="sfs-summary">
            <div className="sfs-summary-item"><strong>{data.summary.total}</strong><span>Total</span></div>
            {Object.entries(statusCounts).sort(([a], [b]) => (STATUS_ORDER[a] ?? 99) - (STATUS_ORDER[b] ?? 99)).map(([status, count]) => (
              <div className="sfs-summary-item" key={status}><StatusPill status={status} /><strong>{count}</strong></div>
            ))}
          </div>
          <ForumStatusTable forums={data.forums} />
        </>}
        {!loading && <button className="sfs-refresh" onClick={() => { setLoading(true); void load(); }} aria-label="Refresh scraper status"><RefreshCw size={14} /></button>}
      </div>
    </section>
  );
}
